import React, { useState } from 'react'
import '../../style/detailed-view.scss'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { TransitionProps } from '@mui/material/transitions'
import { useAuth0 } from '../../contexts/auth0-context'
import Chip from '@mui/material/Chip'
import SvgIcon from '@mui/material/SvgIcon'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import Linking from '../Linking'
import Stack from '@mui/material/Stack'
import {
    StrategicImperative,
    useDeleteStrategicImperativeMutation,
    useUpdateStrategicImperativeMutation,
} from '../../shared/queries/strategicImperatives'
import { AudienceTags } from '../../shared/components/AudienceTags'
import StrategicImperativeForm from './StrategicImperativeForm'

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />
})

type StrategicImperativeDetailedViewProps = {
    strategicImperative: StrategicImperative
    closePanel: () => void
    editing: boolean
    setEditing: (editing: boolean) => void
    navigationStack: unknown[]
}

export default function StrategicImperativeDetailedView({
    strategicImperative,
    closePanel,
    editing,
    setEditing,
    navigationStack,
}: StrategicImperativeDetailedViewProps) {
    const { isAuthenticated } = useAuth0()

    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)

    const strategicImperativeDisplayName = useStrategicImperativeDisplayName()
    const { mutateAsync: updateStrategicImperative } = useUpdateStrategicImperativeMutation()
    const { mutate: deleteStrategicImperative } = useDeleteStrategicImperativeMutation()

    const audiences = strategicImperative.audiences ?? []

    function deleteThisSI() {
        setOpenDeleteModal(false)
        deleteStrategicImperative(strategicImperative._id, {
            onSuccess: () => closePanel(),
            onError: (error) =>
                setDeleteError(
                    error instanceof Error
                        ? error.message
                        : `Failed to delete this ${strategicImperativeDisplayName.singular}`
                ),
        })
    }

    function handleDeleteModalClose() {
        setOpenDeleteModal(false)
    }

    const previousDataStack = [...navigationStack, { path: '/strategic-imperatives', data: strategicImperative }]

    return (
        <Fade in={isAuthenticated} timeout={800}>
            <Grid container item direction="column" sx={{ p: 0 }}>
                <Dialog
                    open={openDeleteModal}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={handleDeleteModalClose}
                    aria-labelledby="alert-dialog-slide-title6"
                    aria-describedby="alert-dialog-slide-description6"
                >
                    <DialogTitle id="alert-dialog-slide-title6">
                        <Typography variant="h4" sx={{ pt: 1 }}>
                            {`Are you sure you want to delete this ${strategicImperativeDisplayName.singular}?`}
                        </Typography>
                    </DialogTitle>
                    <DialogActions>
                        {/* @ts-expect-error mui type */}
                        <Button variant="outlined-sm-button" onClick={handleDeleteModalClose}>
                            CANCEL
                        </Button>
                        {/* @ts-expect-error mui type */}
                        <Button variant="contained-sm-button" sx={{ mb: 1, mr: 1 }} onClick={deleteThisSI}>
                            DELETE
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={!!deleteError}
                    autoHideDuration={6000}
                    onClose={() => setDeleteError(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setDeleteError(null)} severity="error" sx={{ width: '100%' }}>
                        {deleteError}
                    </Alert>
                </Snackbar>

                {editing ? (
                    <Box sx={{ p: 2 }}>
                        <StrategicImperativeForm
                            mode="Edit"
                            defaultValues={{ name: strategicImperative.name, audiences }}
                            onSubmit={async (updates) => {
                                await updateStrategicImperative({
                                    strategicImperativeId: strategicImperative._id,
                                    updates,
                                })
                                setEditing(false)
                            }}
                            onCancel={() => setEditing(false)}
                            onDelete={() => setOpenDeleteModal(true)}
                        />
                    </Box>
                ) : (
                    <Grid container item>
                        <Grid
                            container
                            item
                            direction="column"
                            sx={{
                                p: 3,
                                borderBottomLeftRadius: '24px',
                                borderBottomRightRadius: '24px',
                                backgroundColor: 'figma.grayscale.tier6',
                                boxShadow: 0,
                            }}
                        >
                            <Grid item>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    {strategicImperativeDisplayName.singular}:
                                </Typography>
                            </Grid>
                            <Grid container item direction="row" spacing={1}>
                                <Grid item xs={10}>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            zIndex: 2,
                                            userSelect: 'text',
                                            cursor: 'text',
                                            padding: '16px 20px',
                                            margin: '-16px -16px',
                                        }}
                                        onClick={(e) => e.stopPropagation()} // prevent collapse on text click
                                    >
                                        <Typography
                                            variant="h2"
                                            onDoubleClick={(e) => {
                                                const range = document.createRange()
                                                const selection = window.getSelection()
                                                range.selectNodeContents(e.currentTarget)
                                                selection?.removeAllRanges()
                                                selection?.addRange(range)
                                            }}
                                        >
                                            {strategicImperative.name}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid // Adding an invisible grid with chip to maintain similar appearance as the VMs and Activities pages
                                    item
                                >
                                    <Chip
                                        sx={{ backgroundColor: 'figma.grayscale.tier6' }}
                                        icon={<SvgIcon inheritViewBox fontSize="inherit" />}
                                        // @ts-expect-error mui type
                                        label={<Typography variant="chip"></Typography>}
                                    />
                                </Grid>
                            </Grid>
                            <Stack gap={1} mt={2}>
                                <Typography variant="h5">Audiences:</Typography>
                                <AudienceTags audiences={audiences} />
                            </Stack>
                        </Grid>

                        <Divider variant="middle" />

                        <Grid container item direction="column" spacing={0}>
                            <Stack direction="row" p={3}>
                                <Linking
                                    id={strategicImperative._id}
                                    type="strategicImperative"
                                    previousDataStack={previousDataStack}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Fade>
    )
}
